import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Card,
  Divider,
  InputLabel,
  Alert,
  Snackbar,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import Sidenav from "../../common/Sidenav";
import Header from "../../common/Header";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { DropzoneArea } from "mui-file-dropzone";
import { config } from "../../../config/config";
import { apiList, invokeApi } from "../../../services/apiServices";
import { useCookies } from "react-cookie";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import IconSidenav from "../../common/IconSidenav";
import JoditEditor from "jodit-react";

const EditFeature = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const featureDataFromState = location.state?.featureData;

  const [featureDetails, setFeatureDetails] = useState({
    featureTitle: "",
    ogUrl: "",
    featureContent: "",
    featuredUrl: "",
    bannerUrl: "",
  });
  const [cookies] = useCookies();
  const [bannerImage, setBannerImage] = useState(null);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [selectedBannerImage, setSelectedBannerImage] = useState("");
  const [selectedFeaturedImage, setSelectedFeaturedImage] = useState("");
  const [bannerImageChanged, setBannerImageChanged] = useState(false);
  const [featuredImageChanged, setFeaturedImageChanged] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("");
  const [showSideNav, setShowSideNav] = useState(false);
  const isMobileScreen = useMediaQuery(
    "(min-width:360px) and (max-width:500px)"
  );
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!featureDataFromState);

  const toggleSideNav = () => {
    setShowSideNav(!showSideNav);
  };

  // Fetch feature data if not passed via state
  useEffect(() => {
    const fetchFeatureData = async () => {
      if (!featureDataFromState && id) {
        try {
          setFetching(true);
          const params = { id: id };
          const response = await invokeApi(
            config.getMyCollege + apiList.getFeature,
            params,
            cookies
          );

          if (response?.status === 200) {
            const featureData = response.data.feature;
            setFeatureDetails({
              featureTitle: featureData.featureTitle || "",
              ogUrl: featureData.ogUrl || "",
              featureContent: featureData.featureContent || "",
              featuredUrl: featureData.featuredUrl || "",
              bannerUrl: featureData.bannerUrl || "",
            });
            setSelectedBannerImage(featureData.bannerUrl || "");
            setSelectedFeaturedImage(featureData.featuredUrl || "");
          } else {
            showSnackbar("Failed to fetch feature data", "error");
            navigate("/feature-manager/feature-list");
          }
        } catch (error) {
          console.error("Error fetching feature data:", error);
          showSnackbar("Error fetching feature data", "error");
          navigate("/feature-manager/feature-list");
        } finally {
          setFetching(false);
        }
      } else if (featureDataFromState) {
        setFeatureDetails({
          featureTitle: featureDataFromState.featureTitle || "",
          ogUrl: featureDataFromState.ogUrl || "",
          featureContent: featureDataFromState.featureContent || "",
          featuredUrl: featureDataFromState.featuredUrl || "",
          bannerUrl: featureDataFromState.bannerUrl || "",
        });
        setSelectedBannerImage(featureDataFromState.bannerUrl || "");
        setSelectedFeaturedImage(featureDataFromState.featuredUrl || "");
      }
    };

    fetchFeatureData();
  }, [id, featureDataFromState, cookies, navigate]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFeatureDetails((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleInputContentChange = (name, value) => {
    setFeatureDetails((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleBannerImage = (files) => {
    if (files && files.length > 0) {
      setBannerImageChanged(true);
      const selectedBannerFile = files[0];
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedBannerImage(reader.result);
        setBannerImage(selectedBannerFile);
      };
      reader.readAsDataURL(selectedBannerFile);
    } else {
      setSelectedBannerImage("");
      setBannerImage(null);
      setBannerImageChanged(false);
    }
  };

  const handleFeaturedImage = (files) => {
    if (files && files.length > 0) {
      setFeaturedImageChanged(true);
      const selectedFeaturedFile = files[0];
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedFeaturedImage(reader.result);
        setFeaturedImage(selectedFeaturedFile);
      };
      reader.readAsDataURL(selectedFeaturedFile);
    } else {
      setSelectedFeaturedImage("");
      setFeaturedImage(null);
      setFeaturedImageChanged(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleAddBannerImage = async () => {
    if (!bannerImage) {
      console.log("No banner image to upload");
      return featureDetails.bannerUrl;
    }

    const formData = new FormData();
    formData.append("banner", bannerImage);
    try {
      console.log("Uploading banner image...");
      const response = await fetch(
        "https://api.reef.org.in/addFeatureBanner",
        {
          method: "POST",
          body: formData,
        }
      );

      if (response?.status >= 200 && response?.status < 300) {
        const responseData = await response.json();
        console.log("Banner upload response:", responseData);
        return responseData.bannerUrl || responseData.imageUrl || "";
      } else {
        console.error("Banner upload failed with status:", response?.status);
        showSnackbar("Failed to add feature banner", "error");
        return featureDetails.bannerUrl;
      }
    } catch (error) {
      console.error("Error uploading banner:", error);
      showSnackbar("Something went wrong. Please try again later.", "error");
      return featureDetails.bannerUrl;
    }
  };

  const handleAddFeaturedImage = async () => {
    if (!featuredImage) {
      console.log("No featured image to upload");
      return featureDetails.featuredUrl;
    }

    const formData = new FormData();
    formData.append("featured", featuredImage);
    try {
      console.log("Uploading featured image...");
      const response = await fetch(
        "https://api.reef.org.in/addFeatureFeaturedImage",
        {
          method: "POST",
          body: formData,
        }
      );

      if (response?.status >= 200 && response?.status < 300) {
        const responseData = await response.json();
        console.log("Featured upload response:", responseData);
        return responseData.imageUrl || responseData.featuredUrl || "";
      } else {
        console.error("Featured upload failed with status:", response?.status);
        showSnackbar("Failed to add feature featured image", "error");
        return featureDetails.featuredUrl;
      }
    } catch (error) {
      console.error("Error uploading featured image:", error);
      showSnackbar("Something went wrong. Please try again later.", "error");
      return featureDetails.featuredUrl;
    }
  };

  const handleUpdateFeature = async (updatedFeatureData) => {
    try {
      setLoading(true);
      console.log("Sending update request with data:", updatedFeatureData);

      const response = await invokeApi(
        config.getMyCollege + apiList.updateFeature,
        updatedFeatureData,
        cookies
      );

      console.log("Update API response:", response);

      if (response?.status >= 200 && response?.status < 300) {
        // Check for success in different possible response formats
        if (
          response.data.responseCode === "200" ||
          response.data.status === "success" ||
          response.data.success === true ||
          response.data.message?.toLowerCase().includes("success")
        ) {
          showSnackbar("Feature updated successfully", "success");
          setTimeout(() => {
            navigate("/feature-manager/feature-list");
          }, 1000);
        } else {
          // If we get here, the API returned 200 but with an error message
          showSnackbar(
            response.data.message || "Update completed but with warnings",
            "warning"
          );
          // Still navigate since the update was technically successful
          setTimeout(() => {
            navigate("/feature-manager/feature-list");
          }, 1500);
        }
      } else {
        showSnackbar("Update failed with status: " + response?.status, "error");
      }
    } catch (error) {
      console.error("Error during updating feature:", error);
      showSnackbar("Something went wrong. Please try again later!!", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureSubmit = async (event) => {
    event.preventDefault();

    console.log("Form submitted", featureDetails);

    // Validation
    if (!featureDetails.featureTitle?.trim()) {
      showSnackbar("Feature title is required", "error");
      return;
    }

    if (!featureDetails.ogUrl?.trim()) {
      showSnackbar("OG URL is required", "error");
      return;
    }

    try {
      console.log("Starting image uploads...");
      console.log("Banner changed:", bannerImageChanged);
      console.log("Featured changed:", featuredImageChanged);

      const [bannerUrl, featuredUrl] = await Promise.all([
        bannerImageChanged
          ? handleAddBannerImage()
          : Promise.resolve(featureDetails.bannerUrl),
        featuredImageChanged
          ? handleAddFeaturedImage()
          : Promise.resolve(featureDetails.featuredUrl),
      ]);

      console.log("Final URLs - Banner:", bannerUrl, "Featured:", featuredUrl);

      const updatedFeatureData = {
        id: id,
        featureTitle: featureDetails.featureTitle.trim(),
        ogUrl: featureDetails.ogUrl.trim(),
        featureContent: featureDetails.featureContent || "",
        featuredUrl: featuredUrl,
        bannerUrl: bannerUrl,
      };

      console.log("Sending data to update API:", updatedFeatureData);
      await handleUpdateFeature(updatedFeatureData);
    } catch (error) {
      console.error("Error while updating feature details:", error);
      showSnackbar("Something went wrong. Please try again later.", "error");
    }
  };

  if (fetching) {
    return (
      <Grid sx={{ display: "flex" }}>
        {isMobileScreen ? (
          <IconSidenav />
        ) : showSideNav ? (
          <IconSidenav />
        ) : (
          <Sidenav />
        )}
        <Grid component="main" sx={{ width: "100%", px: 1 }}>
          <Header toggleSideNav={toggleSideNav} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <CircularProgress />
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <div>
      <Grid sx={{ display: "flex" }}>
        {isMobileScreen ? (
          <IconSidenav />
        ) : showSideNav ? (
          <IconSidenav />
        ) : (
          <Sidenav />
        )}
        <Grid component="main" sx={{ width: "100%", px: 1 }}>
          <Header toggleSideNav={toggleSideNav} />

          <Grid sx={{ width: "100%", px: isMobileScreen ? 1 : 5 }}>
            <Card elevation={1} sx={{ my: 5 }}>
              <Grid sx={{ py: isMobileScreen ? 2 : 2 }}>
                <Typography
                  sx={{
                    mb: 2,
                    mx: isMobileScreen ? 2 : 8,
                    textAlign: "left",
                    fontSize: isMobileScreen ? 14 : 18,
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  Edit Feature
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid
                  container
                  md={12}
                  xs={12}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-around",
                    my: 2,
                  }}
                >
                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      Feature Title *
                    </InputLabel>
                    <TextField
                      placeholder="Enter Feature Title"
                      fullWidth
                      name="featureTitle"
                      value={featureDetails.featureTitle}
                      onChange={handleInputChange}
                      inputProps={{
                        style: {
                          height: isMobileScreen ? "3px" : "8px",
                          fontSize: isMobileScreen ? 12 : 14,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      OG URL *
                    </InputLabel>
                    <TextField
                      placeholder="Enter OG URL"
                      fullWidth
                      name="ogUrl"
                      value={featureDetails.ogUrl}
                      onChange={handleInputChange}
                      inputProps={{
                        style: {
                          height: isMobileScreen ? "3px" : "8px",
                          fontSize: isMobileScreen ? 12 : 14,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      Feature Content
                    </InputLabel>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        my: isMobileScreen ? 2 : 2,
                        mb: isMobileScreen ? 15 : 1,
                      }}
                    >
                      <JoditEditor
                        value={featureDetails.featureContent}
                        onChange={(newValue) =>
                          handleInputContentChange("featureContent", newValue)
                        }
                        tabIndex={1}
                      />
                    </Grid>
                  </Grid>

                  <Grid
                    container
                    md={12}
                    sx={{
                      width: "100%",
                      justifyContent: "space-around",
                      my: 5,
                    }}
                  >
                    <Grid item md={5} xs={11}>
                      <InputLabel
                        sx={{
                          fontWeight: "bold",
                          my: 1,
                          fontSize: isMobileScreen ? 12 : 15,
                        }}
                      >
                        Banner Image
                      </InputLabel>
                      <Grid
                        sx={{
                          my: 2,
                          ".MuiBox-root ": {
                            bgcolor: "#f0f8ff",
                            borderColor: "#1d1a57",
                            minHeight: 0,
                            my: 1,
                            alignItems: "center",
                            justifyContent: "center",
                          },
                        }}
                      >
                        <DropzoneArea
                          filesLimit={1}
                          acceptedFiles={["image/jpeg", "image/png"]}
                          onChange={handleBannerImage}
                          maxFileSize={5000000}
                          showPreviews={true}
                          showAlerts={false}
                          showPreviewsInDropzone={false}
                          showFileNames={false}
                          Icon={(props) => (
                            <CloudUploadIcon
                              style={{
                                color: "#1d1a57",
                                width: 35,
                                height: 35,
                              }}
                              {...props}
                            />
                          )}
                          dropzoneText={
                            <Typography
                              sx={{
                                color: "#1d1a57",
                                fontWeight: 600,
                                fontSize: 16,
                              }}
                            >
                              Upload / Drag files
                            </Typography>
                          }
                          previewText="Selected Banner Image:"
                        />
                      </Grid>
                      <Grid
                        sx={{
                          my: 3,
                          py: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                          height: "auto",
                        }}
                      >
                        {selectedBannerImage && (
                          <img
                            src={selectedBannerImage}
                            alt="BannerUrl"
                            style={{ maxWidth: "90px" }}
                          />
                        )}
                      </Grid>
                    </Grid>
                    <Grid item md={5} xs={11}>
                      <InputLabel
                        sx={{
                          fontWeight: "bold",
                          my: 1,
                          fontSize: isMobileScreen ? 12 : 15,
                        }}
                      >
                        Featured Image
                      </InputLabel>
                      <Grid
                        sx={{
                          my: 2,
                          ".MuiBox-root ": {
                            bgcolor: "#f0f8ff",
                            borderColor: "#1d1a57",
                            minHeight: 0,
                            my: 1,
                            alignItems: "center",
                            justifyContent: "center",
                          },
                        }}
                      >
                        <DropzoneArea
                          filesLimit={1}
                          acceptedFiles={["image/jpeg", "image/png"]}
                          onChange={handleFeaturedImage}
                          maxFileSize={5000000}
                          showPreviews={true}
                          showAlerts={false}
                          showPreviewsInDropzone={false}
                          showFileNames={false}
                          Icon={(props) => (
                            <CloudUploadIcon
                              style={{
                                color: "#1d1a57",
                                width: 35,
                                height: 35,
                              }}
                              {...props}
                            />
                          )}
                          dropzoneText={
                            <Typography
                              sx={{
                                color: "#1d1a57",
                                fontWeight: 600,
                                fontSize: 16,
                              }}
                            >
                              Upload / Drag files
                            </Typography>
                          }
                          previewText="Selected Featured Image:"
                        />
                      </Grid>
                      <Grid
                        sx={{
                          my: 3,
                          py: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                          height: "auto",
                        }}
                      >
                        {selectedFeaturedImage && (
                          <img
                            src={selectedFeaturedImage}
                            alt="FeaturedUrl"
                            style={{ maxWidth: "90px" }}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Divider sx={{ mb: 1 }} />
                <Grid
                  container
                  md={12}
                  xs={12}
                  sx={{
                    width: "100%",
                  }}
                >
                  <Divider sx={{ mb: 1 }} />
                  <Grid
                    container
                    spacing={isMobileScreen ? 0 : 2}
                    md={12}
                    xs={12}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mt: 2,
                    }}
                  >
                    <Button
                      color="success"
                      sx={{
                        mr: 4,
                        my: 2,
                        px: 2,
                        fontSize: 12,
                        background: "#1d1a57",
                        color: "#FFF",
                        fontWeight: 600,
                        "&:hover": {
                          background: "#1d1a57",
                          color: "#FFF",
                        },
                      }}
                      onClick={handleFeatureSubmit}
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Save Changes"}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Card>
            <Snackbar
              open={openSnackbar}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ width: "auto" }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={snackbarSeverity}
                sx={{ width: "auto" }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default EditFeature;
